import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { BdsBanner, BdsSelect, BdsTableCell, BdsTableRow, BdsTypo } from 'blip-ds/dist/blip-ds-react';
import SessionConsumptionContext from '../../../../contexts/SessionConsumptionContext';
import { getMonthTwoDigits, toUTCDateString } from '../../../../utils/date';
import { PageHeaderComponent } from '../../components/Metrics/PageHeader/PageHeaderComponent';
import { trackEvent } from '../../../../api/Billing/Tracking';
import '../../../../typings/blip-ds.d';
import FeatureToggleService from '../../../../services/FeatureToggleService';
import {
  SessionConsumptionPaper,
  SessionConsumptionRow,
  SessionConsumptionHeaderFilter,
} from '../styles';
import {
  action,
  origin,
  REPORT_PERIOD_SELECT,
  REPORT_SESSION_CONSUMPTION_CONVERSATION_SELECT,
  REPORT_SESSION_CONSUMPTION_PER_DATE_BUSINESSINITIATED_TAB_CLICK,
  REPORT_SESSION_CONSUMPTION_PER_DATE_USERINITIATED_TAB_CLICK,
  REPORT_SESSION_CONSUMPTION_WABAID_SELECT,
} from '../../../../shared/SegmentEvents';
import { BillingMetrics } from '../../../../shared/BillingMetrics';
import TableWithPagination from '../../../../components/TableWithPagination/TableWithPagination';
import { useSortedPaginatedData } from '../../../../hooks/useSortedPaginatedData';
import { DEFAULT_PAGE_OPTIONS, COLUMN_WIDTHS_7 as BUSINESS_COLUMN_WIDTHS, COLUMN_WIDTHS_3 as USER_COLUMN_WIDTHS } from '../../../../utils/paginationUtils';

const BUSINESS_COLUMN_KEYS = ['dateReference', 'utility', 'authentication', 'marketing', 'marketing_lite', 'service', 'total'];
const USER_COLUMN_KEYS = ['dateReference', 'free', 'total'];
const OLD_COLUMN_KEYS = ['dateReference', 'sessionByUser', 'sessionByCompany', 'sessionTotal'];
const OLD_COLUMN_WIDTHS = USER_COLUMN_WIDTHS;

export interface SessionConsumptionDailyUsageProps {
  isDetailedView?: boolean;
}

const SessionConsumptionDailyUsageComponent: FC<SessionConsumptionDailyUsageProps> = ({
  isDetailedView = false,
}) => {
  const {
    dateToSearch,
    content,
    tenant,
    loggedUser,
    trackingProps,
    getPlanStatus,
    billingSessionConsumptionApi,
    oldSessionConsumptionDailyUsage,
    setOldSessionConsumptionDailyUsage,
    periodOptions,
    setDateToSearch,
    conversationTypeFilter,
    conversationWabaFilter,
    conversationTypeFilterOptions,
    conversationWabaFilterOptions,
    setConversationTypeFilter,
    setConversationWabaFilter,
    isWabaloading,
    WHATSAPP_BP_PRICING_CHANGES_START_DATE,
    showSessionNewPricing,
    setShowSessionNewPricing,
  } = useContext(SessionConsumptionContext);
  const [isLoading, setLoading] = useState(false);
  const [businessInitiatedDailyUsage, setBusinessInitiatedDailyUsage] = useState([] as any);
  const [userInitiatedDailyUsage, setUserInitiatedDailyUsage] = useState([] as any);

  const {
    itemsPerPage: businessItemsPerPage,
    sortConfig: businessSortConfig,
    sortedData: sortedBusinessData,
    paginatedData: paginatedBusinessData,
    handlePaginationChange: handleBusinessPaginationChange,
    handleItemsPerPageChange: handleBusinessItemsPerPageChange,
    handleSortChange: handleBusinessSortChange,
  } = useSortedPaginatedData({ data: businessInitiatedDailyUsage });

  const {
    itemsPerPage: userItemsPerPage,
    sortConfig: userSortConfig,
    sortedData: sortedUserData,
    paginatedData: paginatedUserData,
    handlePaginationChange: handleUserPaginationChange,
    handleItemsPerPageChange: handleUserItemsPerPageChange,
    handleSortChange: handleUserSortChange,
  } = useSortedPaginatedData({ data: userInitiatedDailyUsage });

  const {
    itemsPerPage: oldItemsPerPage,
    sortConfig: oldSortConfig,
    sortedData: sortedOldData,
    paginatedData: paginatedOldData,
    handlePaginationChange: handleOldPaginationChange,
    handleItemsPerPageChange: handleOldItemsPerPageChange,
    handleSortChange: handleOldSortChange,
  } = useSortedPaginatedData({ data: oldSessionConsumptionDailyUsage });

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);
        const monthString = `${dateToSearch.getFullYear()}-${getMonthTwoDigits(dateToSearch)}`;
        const showNewPricing = dateToSearch >= WHATSAPP_BP_PRICING_CHANGES_START_DATE;
        setShowSessionNewPricing(showNewPricing);
        const storageType =
          (await FeatureToggleService.isDatabricksStorageTypeEnabled(BillingMetrics.session)) ? 1 : 0;

        if (showNewPricing) {
          const data = await billingSessionConsumptionApi.getDailySessionConsumption(
            tenant.info.id,
            monthString,
            -1,
            conversationWabaFilter,
            storageType,
          );
          setBusinessInitiatedDailyUsage(
            data.map((obj) => ({
              dateReference: toUTCDateString(obj.dateReference, content.locale),
              utility: obj.businessInitiated.utility,
              authentication: obj.businessInitiated.authentication,
              marketing: obj.businessInitiated.marketing,
              marketing_lite: obj.businessInitiated.marketing_lite,
              service: obj.businessInitiated.service,
              total: obj.businessInitiated.total,
            })),
          );
          setUserInitiatedDailyUsage(
            data.map((obj) => ({
              dateReference: toUTCDateString(obj.dateReference, content.locale),
              free: obj.userInitiated.free,
              total: obj.userInitiated.total,
            })),
          );
        } else {
          const oldData = await billingSessionConsumptionApi.getOldDailySessionConsumption(
            tenant.info.id,
            monthString,
            -1,
            conversationTypeFilter,
            conversationWabaFilter,
            '',
          );
          setOldSessionConsumptionDailyUsage(
            oldData.map((obj) => ({
              ...obj,
              dateReference: toUTCDateString(obj.dateReference, content.locale),
            })),
          );
        }
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [dateToSearch, conversationTypeFilter, conversationWabaFilter]);

  const businessRows = useMemo(
    () =>
      paginatedBusinessData.length > 0
        ? paginatedBusinessData.map((item) => (
            <BdsTableRow key={item.dateReference + '-business'}>
              <BdsTableCell>
                <BdsTypo variant="fs-14" bold="bold" margin={false}>
                  {item.dateReference}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>{item.utility}</BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>{item.authentication}</BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>{item.marketing}</BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>{item.marketing_lite}</BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>{item.service}</BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>{item.total}</BdsTypo>
              </BdsTableCell>
            </BdsTableRow>
          ))
        : undefined,
    [paginatedBusinessData],
  );

  const userRows = useMemo(
    () =>
      paginatedUserData.length > 0
        ? paginatedUserData.map((item) => (
            <BdsTableRow key={item.dateReference + '-user'}>
              <BdsTableCell>
                <BdsTypo variant="fs-14" bold="bold" margin={false}>
                  {item.dateReference}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>{item.free}</BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>{item.total}</BdsTypo>
              </BdsTableCell>
            </BdsTableRow>
          ))
        : undefined,
    [paginatedUserData],
  );

  const oldRows = useMemo(
    () =>
      paginatedOldData.length > 0
        ? paginatedOldData.map((item) => (
            <BdsTableRow key={item.dateReference}>
              <BdsTableCell>
                <BdsTypo variant="fs-14" bold="bold" margin={false}>
                  {item.dateReference}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>{item.sessionByUser}</BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>{item.sessionByCompany}</BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>{item.sessionTotal}</BdsTypo>
              </BdsTableCell>
            </BdsTableRow>
          ))
        : undefined,
    [paginatedOldData],
  );

  const periodOptionChangeEvent = async (selectedOption) => {
    const dateToRetrieveData = new Date(selectedOption.detail.value);
    setDateToSearch(dateToRetrieveData);
    const planStatus = await getPlanStatus();
    trackEvent(REPORT_PERIOD_SELECT, tenant, loggedUser, {
      ...trackingProps,
      date: dateToRetrieveData,
      planStatus,
      origin: origin.SESSIONPERDATE,
    });
  };

  const wabaOptionChangeEvent = (selectedOption) => {
    const conversationWabaValue = selectedOption.detail.value;
    setConversationWabaFilter(conversationWabaValue);
    trackEvent(REPORT_SESSION_CONSUMPTION_WABAID_SELECT, tenant, loggedUser, {
      ...trackingProps,
      whatsappConversationWaba: conversationWabaValue,
      origin: origin.SESSIONPERDATE,
    });
  };

  const typeOptionChangeEvent = (selectedOption) => {
    const conversationTypeValue = selectedOption.detail.value;
    setConversationTypeFilter(conversationTypeValue);
    trackEvent(REPORT_SESSION_CONSUMPTION_CONVERSATION_SELECT, tenant, loggedUser, {
      ...trackingProps,
      whatsappConversationType: conversationTypeValue,
      origin: origin.SESSIONPERDATE,
    });
  };

  return (
    <>
      {isDetailedView && (
        <PageHeaderComponent
          title={content.sessionDailyConsumption.title}
          dateToSearch={dateToSearch}
          periodOptions={periodOptions}
          periodOptionChangeEvent={periodOptionChangeEvent}
        />
      )}
      <SessionConsumptionPaper>
        <SessionConsumptionRow isDetailedView={isDetailedView}>
          {!isDetailedView && (
            <BdsTypo variant="fs-24" bold="bold" margin={false}>
              {content.sessionDailyConsumption.title}
            </BdsTypo>
          )}
          <BdsTypo variant="fs-14" margin={false}>
            {content.sessionDailyConsumption.about}
          </BdsTypo>
          {isDetailedView && (
            <SessionConsumptionHeaderFilter>
              {conversationWabaFilterOptions.length > 0 && (
                <BdsSelect
                  disabled={conversationWabaFilterOptions.length === 1}
                  value={conversationWabaFilter}
                  label={content.conversationWabaLabel}
                  onBdsChange={wabaOptionChangeEvent}
                >
                  {conversationWabaFilterOptions}
                </BdsSelect>
              )}
              {!showSessionNewPricing && (
                <BdsSelect
                  value={conversationTypeFilter}
                  label={content.conversationTypeLabel}
                  onBdsChange={typeOptionChangeEvent}
                >
                  {conversationTypeFilterOptions}
                </BdsSelect>
              )}
            </SessionConsumptionHeaderFilter>
          )}
        </SessionConsumptionRow>
        {showSessionNewPricing ? (
          <bds-tab-group
            align="left"
            content-scrollable="false"
            onClick={(e) => {
              try {
                const isActiveFirstTab = e.target.firstChild.getAttribute('open') === '';
                if (isActiveFirstTab) {
                  trackEvent(REPORT_SESSION_CONSUMPTION_PER_DATE_USERINITIATED_TAB_CLICK, tenant, loggedUser, {
                    ...trackingProps,
                    action: action.CLICK,
                    origin: origin.SESSIONPERDATE,
                  });
                } else {
                  trackEvent(REPORT_SESSION_CONSUMPTION_PER_DATE_BUSINESSINITIATED_TAB_CLICK, tenant, loggedUser, {
                    ...trackingProps,
                    action: action.CLICK,
                    origin: origin.SESSIONPERDATE,
                  });
                }
              } catch (error) {}
            }}
          >
            <bds-tab-item label={content.sessionConsumptionByDate.businessInitiated.tab}>
              <BdsBanner variant="system" context="inside">
                {content.sessionConsumptionByPhone.businessInitiated.bannerText}
              </BdsBanner>
              <TableWithPagination
                options={{
                  heading: [
                    content.sessionConsumptionByDate.columnDateReference,
                    content.sessionConsumptionByDate.businessInitiated.columnUtility,
                    content.sessionConsumptionByDate.businessInitiated.columnAuthentication,
                    content.sessionConsumptionByDate.businessInitiated.columnMarketing,
                    content.sessionConsumptionByDate.businessInitiated.columnMarketingLite,
                    content.sessionConsumptionByDate.businessInitiated.columnService,
                    content.sessionConsumptionByDate.columnTotal,
                  ],
                  columnKeys: BUSINESS_COLUMN_KEYS,
                  columnWidths: BUSINESS_COLUMN_WIDTHS,
                  sortConfig: businessSortConfig,
                  onSortChange: handleBusinessSortChange,
                  itemsPerPageList: DEFAULT_PAGE_OPTIONS,
                  itemsPerPage: businessItemsPerPage,
                  numberOfItems: isLoading || isWabaloading ? businessItemsPerPage : sortedBusinessData.length,
                  isLoading: isLoading || isWabaloading,
                  hasEmptyState: !(isLoading || isWabaloading) && sortedBusinessData.length === 0,
                  emptyStateSubtitle: '',
                  isAutoHeight: true,
                }}
                onPaginationChange={handleBusinessPaginationChange}
                onItemsPerPageChange={handleBusinessItemsPerPageChange}
              >
                {businessRows}
              </TableWithPagination>
            </bds-tab-item>
            <bds-tab-item label={content.sessionConsumptionByDate.userInitiated.tab}>
              <bds-grid margin="t-3">
                <TableWithPagination
                  options={{
                    heading: [
                      content.sessionConsumptionByDate.columnDateReference,
                      content.sessionConsumptionByDate.userInitiated.free,
                      content.sessionConsumptionByDate.columnTotal,
                    ],
                    columnKeys: USER_COLUMN_KEYS,
                    columnWidths: USER_COLUMN_WIDTHS,
                    sortConfig: userSortConfig,
                    onSortChange: handleUserSortChange,
                    itemsPerPageList: DEFAULT_PAGE_OPTIONS,
                    itemsPerPage: userItemsPerPage,
                    numberOfItems: isLoading || isWabaloading ? userItemsPerPage : sortedUserData.length,
                    isLoading: isLoading || isWabaloading,
                    hasEmptyState: !(isLoading || isWabaloading) && sortedUserData.length === 0,
                    emptyStateSubtitle: '',
                    isAutoHeight: true,
                  }}
                  onPaginationChange={handleUserPaginationChange}
                  onItemsPerPageChange={handleUserItemsPerPageChange}
                >
                  {userRows}
                </TableWithPagination>
              </bds-grid>
            </bds-tab-item>
          </bds-tab-group>
        ) : (
          <TableWithPagination
            options={{
              heading: [
                content.sessionDailyConsumption.columnDateReference,
                content.sessionDailyConsumption.columnUserSession,
                content.sessionDailyConsumption.columnBusinessSession,
                content.sessionDailyConsumption.columnTotal,
              ],
              columnKeys: OLD_COLUMN_KEYS,
              columnWidths: OLD_COLUMN_WIDTHS,
              sortConfig: oldSortConfig,
              onSortChange: handleOldSortChange,
              itemsPerPageList: DEFAULT_PAGE_OPTIONS,
              itemsPerPage: oldItemsPerPage,
              numberOfItems: isLoading || isWabaloading ? oldItemsPerPage : sortedOldData.length,
              isLoading: isLoading || isWabaloading,
              hasEmptyState: !(isLoading || isWabaloading) && sortedOldData.length === 0,
              emptyStateSubtitle: '',
              isAutoHeight: true,
            }}
            onPaginationChange={handleOldPaginationChange}
            onItemsPerPageChange={handleOldItemsPerPageChange}
          >
            {oldRows}
          </TableWithPagination>
        )}
      </SessionConsumptionPaper>
    </>
  );
};

export default SessionConsumptionDailyUsageComponent;
